import { computed, reactive, ref } from 'vue';

import { defineStore } from 'pinia';

import moment from 'moment';

import { AlertType } from '@/constants/const';
import { Keys } from '@/constants/key';
import { getAlerts } from '@/hooks/services/alert-cards.service';
import { useAuthPage } from '@/hooks/useAuthPage';
import { Alert } from '@/interfaces/alert';
import { GroupTotalResponseData } from '@/schema/api/resource.schema';
import { getSection } from '@/utils/getter';
import { SchemaType } from '@/utils/types';

// const logger = new Logger('card', {
//   isDebug: false,
// });

export const useAlertStore = defineStore(Keys.STORE.ALERT, () => {
  const INTERVAL_TIME = 1000 * 5;

  let isStarted = false; // start action 발생시에만 true로 설정 됩니다.

  const { isAuthorized } = useAuthPage();
  const alerts = reactive<Record<number, Alert[]>>({});

  const controlGroupIds = ref<number[]>([]);

  const setControlGroupIds = (ids: number[]) => {
    controlGroupIds.value = ids;
  };

  const curSection = computed(() => {
    return getSection(moment());
  });

  const selectedGroupId = ref(-1);
  const setSelectedGroupId = (groupId: number) => {
    selectedGroupId.value = groupId;
  };

  const exposeAlerts = computed<Alert[] | null>(() => {
    return alerts[selectedGroupId.value] ?? null;
  });

  const isExistAlert = computed(() => {
    return Object.values(alerts).reduce((acc, cur) => {
      if (cur.length === 0) return acc;
      return acc || cur[0].control.type !== AlertType.NONE || cur[1].control.type !== AlertType.NONE;
    }, false);
  });

  const setAlertData = async (list: SchemaType<typeof GroupTotalResponseData>) => {
    if (!isAuthorized()) {
      return;
    }

    const controlGroupList = list.filter(e => controlGroupIds.value.includes(e.id));

    for (const item of controlGroupList) {
      const groupId = item.id;
      const groupName = item.name;

      alerts[groupId] = await getAlerts(groupId, groupName);
    }
  };

  let startEffectFn: Function | null = null;
  let closeEffectFn: Function | null = null;

  const reset = () => {
    closeEffectFn && closeEffectFn();
    closeEffectFn = null;
    startEffectFn = null;
  };

  /**
   * start는 end가 호출되기 전까지는 재호출 하지 않아야 합니다.
   */
  const start = (groupList: SchemaType<typeof GroupTotalResponseData>) => {
    // start는 1회만 호출되도록 합니다.
    if (isStarted) {
      console.warn('Alert is already started');
      return;
    }

    isStarted = true;
    reset();
    startEffectFn = () => {
      let intervalId: NodeJS.Timeout | null = null;

      const startIntervalId = setInterval(() => {
        // start 상태면서 제어 그룹이 있는 경우 1회 실행후 interval 시작
        if (!isStarted) {
          clearInterval(startIntervalId);
        } else if (controlGroupIds.value.length > 0) {
          setAlertData(groupList);
          intervalId = setInterval(() => setAlertData(groupList), INTERVAL_TIME);
          clearInterval(startIntervalId);
        }
      }, 10);

      return () => intervalId && clearInterval(intervalId);
    };
    closeEffectFn = startEffectFn();
  };

  const end = () => {
    reset();
    for (const groupId in alerts) {
      alerts[groupId] = [];
    }
    isStarted = false;
  };

  return {
    curSection,
    exposeAlerts,
    isExistAlert,
    setSelectedGroupId,
    setControlGroupIds,
    start,
    end,
  };
});
